import Application from '../Application';
import BaseView, { DomainModel3D } from './BaseView';
import SectionView from './SectionView';
import logging from '../logging';
import pc from '../ParametricCache';
import { ClosetUnit } from '../domain';
import { Mesh, BufferGeometry, Material } from 'three';

const log = logging('view');

export default class ClosetUnitView extends BaseView {

    declare model: ClosetUnit;

    private leftPanel: Mesh;
    private rightPanel: Mesh;
    private topShelf: Mesh;
    private bottomShelf: Mesh;
    private toeKick: Mesh;

    private content: SectionView;

    highlights: Set<Mesh> = new Set();

    constructor(model: DomainModel3D, app: Application) {
        super(model, app);
        this.init();
    }

    async make() {

        this.userData.isParametric = true;
        this.name = 'ClosetUnit';

        const empty = new Mesh(new BufferGeometry(), new Material());
        empty.castShadow = empty.receiveShadow = true;
        this.leftPanel = empty;
        this.rightPanel = empty.clone();
        this.topShelf = empty.clone();
        this.bottomShelf = empty.clone();
        this.toeKick = empty.clone();
        this.content = new SectionView(this.model.content, this.app);

        this.add(this.leftPanel);
        this.add(this.rightPanel);
        this.add(this.topShelf);
        this.add(this.bottomShelf);
        this.add(this.toeKick);
        this.add(this.content);
    }

    protected bind(): void {
        const changeSize = this.changeSize.bind(this);
        this.model.bind('width', changeSize);
        this.model.on('height', changeSize);
        this.model.on('depth', changeSize);
        this.model.on('thickness', changeSize);
        this.model.on('bottomShelf', changeSize);
        this.model.on('topShelf', changeSize);
        this.model.on('toeKick', changeSize);
        this.model.on(
            'thickness', () => this.model.content.trigger('thickness'));

        this.bindMaterialArray(this.leftPanel, this.model.materials);
        this.bindMaterialArray(this.rightPanel, this.model.materials);
        this.bindMaterialArray(this.topShelf, this.model.materials);
        this.bindMaterialArray(this.bottomShelf, this.model.materials);
        this.bindMaterialArray(this.toeKick, this.model.materials);
    }

    private changeSize() {
        const thickness = this.model.thickness;

        this.rightPanel.position.set(this.model.width+thickness, 0, 0);
        this.topShelf.position.set(thickness, this.model.height-thickness, 0);
        this.bottomShelf.position.set(thickness, this.model.bottomShelf - 3/8, 0);
        this.toeKick.position.set(thickness, 0, 0);

        this.updates.queue(
            async () => {
//                const left = await pc.make(
//                    'VerticalBoringPanel', 
//                    'boring_panel_half',
//                    this.model.height,
//                    this.model.depth
//                );
//                const right = mirror(left, AXIS_X);
                const left = await pc.make(
                    'Divider', this.model.height, this.model.depth);
                const right = left.clone();

                const top = await pc.make(
                    'FixedShelf', this.model.width, this.model.depth);
                const bottom = await pc.make(
                    'FixedShelf', this.model.width, this.model.depth);

                let toeKick: BufferGeometry | undefined;
                if(this.model.toeKick) {
                    toeKick = await pc.make(
                        'ToeKick',
                        this.model.width,
                        this.model.bottomShelf - 3/8,
                        this.model.depth - 3);
                }

                return { left, right, top, bottom, toeKick };
            },
            ({ left, right, top, bottom, toeKick }) => {
                this.leftPanel.geometry = left;
                this.rightPanel.geometry = right;
                this.topShelf.geometry = top;
                this.bottomShelf.geometry = bottom;
                if(toeKick) {
                    this.toeKick.geometry = toeKick;
                } else {
                    this.toeKick.geometry = new BufferGeometry();
                }

                const thicknessScale = thickness / 0.75;

                this.leftPanel.scale.set(thicknessScale, 1, 1);
                this.rightPanel.scale.set(thicknessScale, 1, 1);
                this.topShelf.scale.set(1, thicknessScale, 1);
                this.bottomShelf.scale.set(1, thicknessScale, 1);

                // The bottomShelf measurement is to the bottom of the bottom
                // shelf, not to its hole. If the bottom shelf is at 0,
                // then its pin is at 3/8". If it is at 32mm, then its pin is
                // in the hole at 3/8" + 32mm, but the bottomShelf measurment
                // still looks like 32mm. This is different from fixed shelves
                // which are measured from 3/8" above their bottom surface.
                // Note that 32mm = ~1.26".
                this.toeKick.visible = this.model.bottomShelf > 1.2;
                
                this.layoutContent();
                this.pipelineUpdate();
            }
        );
    }

    layoutContent() {
        const thickness = this.model.thickness;

        this.model.content.height =
            this.model.height - this.model.bottomShelf + 3/8 - 2*thickness;
        this.model.content.width = this.model.width;
        this.model.content.depth = this.model.depth;
        this.model.content.position.x = thickness;
        this.model.content.position.y = this.model.bottomShelf -3/8 + thickness;
        this.model.content.position.z = 0;
    }
}
